import { FC } from "react";
import { Col, Empty, Pagination, Skeleton, Table as AntTable } from "antd";
import "./table.less";

interface ITableColumn {
  title: string;
  dataIndex: string;
  key: string;
  width?: number;
}

interface ITable {
  data: Array<Record<string, any>>;
  columns: ITableColumn[];
  onRowClick?: (record: Record<string, any>) => void;
  locale?: any;
  isLoading?: boolean;
  page?: number;
  pageSize?: number; // ✅ new, optional
  pagination?: boolean;
  onChangePage?: (page: number, pageSize?: number) => void; // ✅ backward compatible
  count?: number;
  scroll?: number;
  scrollY?: number;
  uniqueClass?: string;
  rowKey?: string;
}

const Table: FC<ITable> = ({
  columns,
  data,
  onRowClick,
  pagination = false,
  isLoading,
  page = 1,
  pageSize = 10, // ✅ fallback so old code still works
  count = 10,
  onChangePage,
  locale,
  scroll,
  scrollY,
  uniqueClass = "table2",
  rowKey = "id",
}) => {
  const lastColumn = columns[columns.length - 1];
  const mainColumns = columns.slice(0, -1);

  const loadingLocale = {
    emptyText: isLoading ? (
      <>
        {[1, 2, 3, 4].map(x => (
          <Skeleton.Input key={x} size="small" active />
        ))}
      </>
    ) : (
      <Empty />
    ),
  };

  return (
    <Col span={24}>
      <div className="tables-container">
        {/* Main Table */}
        <div className="main-table">
          <AntTable
            onRow={(record: Record<string, any>) => ({
              onClick: () => onRowClick?.(record),
            })}
            className={`table ${
              onRowClick ? "onRowHover cursor-pointer" : ""
            } ${
              window.location.pathname.includes("client") ? "table--client" : ""
            }`}
            scroll={{ x: scroll || "max-content", y: scrollY || "100%" }}
            columns={mainColumns.map(col => ({
              ...col,
              width: col.width || 100,
            }))}
            dataSource={isLoading ? [] : data}
            pagination={false}
            rowKey={rowKey}
            locale={!data?.length && !isLoading ? locale : loadingLocale}
          />
        </div>

        {/* Last Column Table */}
        {lastColumn && (
          <div className="last-column-table">
            <AntTable
              className="last-column-table-inner"
              columns={[lastColumn]}
              dataSource={isLoading ? [] : data}
              pagination={false}
              rowKey={rowKey}
              scroll={{ x: scroll || "max-content", y: scrollY || "100%" }}
              locale={!data?.length && !isLoading ? locale : loadingLocale}
            />
          </div>
        )}
      </div>

      {/* Pagination */}
      {data?.length && pagination && (
        <div className="table__pagination">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={count}
            showSizeChanger
            pageSizeOptions={["10", "20", "50", "100"]}
            onChange={(newPage, newSize) => {
              onChangePage?.(newPage, newSize); // ✅ supports old & new
            }}
            onShowSizeChange={(newPage, newSize) => {
              onChangePage?.(newPage, newSize); // ✅ pageSize change
            }}
          />
        </div>
      )}
    </Col>
  );
};

export default Table;
